import React from 'react';

import { WarningOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import {
  TenantFeatureFlag,
  ValidationRuleTemplateCategory,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import {
  ValidationRuleCategories,
  ValidationRuleOverrideColumn,
} from '@recurrency/core-api-schema/dist/validationRules/common';
import { Checkbox } from 'antd';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { AsyncButton } from 'components/Button/AsyncButton';
import { FlexSpace } from 'components/FlexSpace';
import { FlexSpacer } from 'components/FlexSpacer';
import { Input } from 'components/Input';
import { Modal } from 'components/Modal';
import { PropertyListItem } from 'components/PropertyListItem';
import { Select } from 'components/Select';
import { TextArea } from 'components/TextArea';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { shouldShowFeatureFlag } from 'utils/roleAndTenant';
import { getTenantSetting } from 'utils/tenantSettings';

import { getCategoryDisplayName, handleOnCopy } from '../utils';

export interface EditRuleOrTemplateUpdate {
  name: string;
  description: string;
  sql: string;
  isDailyWorkflow?: boolean;
  isOverridesEnabled?: boolean;
  internalOnly?: boolean;
  category?: ValidationRuleTemplateCategory;
  ruleCategory?: ValidationRuleCategories;
}

export function UpsertRuleOrTemplateModal({
  rule,
  onClose,
  onSubmit,
  template,
  hideSql,
}: {
  rule?: EditRuleOrTemplateUpdate;
  onClose: () => void;
  onSubmit: (updates: EditRuleOrTemplateUpdate) => Promise<void>;
  template?: boolean;
  hideSql?: boolean;
}) {
  const [updates, setUpdates] = React.useState<EditRuleOrTemplateUpdate>({
    sql: rule?.sql || '',
    name: rule?.name || '',
    description: rule?.description || '',
    isDailyWorkflow: rule?.isDailyWorkflow || false,
    isOverridesEnabled: rule?.isOverridesEnabled || false,
    internalOnly: rule?.internalOnly || false,
    category: rule?.category || undefined,
    ruleCategory: rule?.ruleCategory || ValidationRuleCategories.PlanningFilters,
  });
  const { activeTenant, activeUser } = useGlobalApp();

  const showOverrideRules = shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.FeatureOverrideRules);
  const showOverrideSqlWarning =
    showOverrideRules &&
    updates.isOverridesEnabled &&
    !Object.values(ValidationRuleOverrideColumn).some((field) => updates.sql.includes(field));

  // Min/Max Automation rules only select item-locations for the nightly automation to export, so the
  // category is only offered where that is turned on. An existing rule keeps its category either way.
  const showMinMaxAutomation =
    Boolean(getTenantSetting(TenantSettingKey.FeatureAutomatedMinMax)) ||
    updates.ruleCategory === ValidationRuleCategories.MinMaxAutomation;
  const categoryOptions = Object.values(ValidationRuleCategories)
    .filter((category) => showMinMaxAutomation || category !== ValidationRuleCategories.MinMaxAutomation)
    .map((category) => ({ label: getCategoryDisplayName(category), value: category }));

  async function handleSubmit() {
    const updatePayload = JSON.parse(JSON.stringify(updates));
    if (rule?.sql && rule.sql === updates.sql) {
      delete updatePayload.sql;
    }
    await onSubmit(updatePayload);
    onClose();
  }

  return (
    <Modal
      visible
      title={
        rule
          ? `Edit ${template ? 'Template' : 'Rule'} Details [${rule?.name}]`
          : `Create New ${template ? 'Template' : 'Rule'}`
      }
      onCancel={() => onClose()}
      width={900}
      destroyOnClose
      footer={
        <>
          <Button onClick={() => onClose()}>Cancel</Button>
          <AsyncButton
            onClick={() => handleSubmit()}
            type="primary"
            permissionsRequired={[TenantUserPermission.ActionCreateRules]}
            disabled={updates.sql.length === 0 || updates.name.length === 0 || updates.description.length === 0}
          >
            {rule ? `Save Changes` : `Create ${template ? 'Template' : 'Rule'}`}
          </AsyncButton>
        </>
      }
    >
      <PropertyListItem
        label="Name"
        value={<Input value={updates.name} onChange={(ev) => setUpdates({ ...updates, name: ev.target.value })} />}
      />

      <PropertyListItem
        label="Description"
        value={
          <TextArea
            rows={2}
            name="textValue"
            wrap="soft"
            value={updates.description}
            onChange={(ev) => setUpdates({ ...updates, description: ev.target.value })}
          />
        }
      />

      {!hideSql && (
        <>
          <PropertyListItem
            label="SQL"
            value={
              <TextArea
                rows={8}
                name="textValue"
                wrap="soft"
                value={updates.sql}
                onChange={(ev) => setUpdates({ ...updates, sql: ev.target.value })}
              />
            }
          />
          {showOverrideSqlWarning && (
            <Alert
              message={`Override rule SQL must contain at least one of the following: \
                ${[
                  ...Object.values(ValidationRuleOverrideColumn),
                  `${ValidationRuleOverrideColumn.OverrideForecastMonth}0-17`,
                  `${ValidationRuleOverrideColumn.OverrideUsageMonth}0-59`,
                ]
                  .filter(
                    (f) =>
                      f !== ValidationRuleOverrideColumn.OverrideForecastMonth &&
                      f !== ValidationRuleOverrideColumn.OverrideUsageMonth,
                  )
                  .join(', ')}`} // replace override_forecast_month with month index
              icon={<WarningOutlined />}
              type="warning"
              banner
              className={css`
                margin-top: 16px;
              `}
            />
          )}
        </>
      )}
      {template && (
        <PropertyListItem
          style={{ flexGrow: 1, marginTop: 12 }}
          label="Category"
          value={
            <Select
              style={{ width: '100%' }}
              options={Object.values(ValidationRuleTemplateCategory)
                .filter((cat) => cat !== ValidationRuleTemplateCategory.MostPopular)
                .map((category) => ({
                  label: category,
                  value: category,
                }))}
              value={updates.category}
              onChange={(ev) => setUpdates({ ...updates, category: ev })}
            />
          }
        />
      )}
      <PropertyListItem
        style={{ flexGrow: 1, marginTop: 12 }}
        label={template ? 'Default Rule Bucket' : 'Category'}
        value={
          <Select
            style={{ width: '100%' }}
            options={categoryOptions}
            value={updates.ruleCategory}
            onChange={(ev: ValidationRuleCategories) =>
              setUpdates({
                ...updates,
                ruleCategory: ev,
                isDailyWorkflow: ev === ValidationRuleCategories.DailyWorkflow,
              })
            }
          />
        }
      />
      <FlexSpace direction="row" align-items="center" gap={8}>
        {!hideSql && (
          <Button onClick={() => handleOnCopy(updates.sql)} type="dashed" style={{ marginTop: 10 }}>
            Copy Runnable SQL
          </Button>
        )}
        <FlexSpacer />
        {showOverrideRules && !template && (
          <>
            <Checkbox
              checked={updates.isOverridesEnabled}
              onChange={(ev) => setUpdates({ ...updates, isOverridesEnabled: ev.target.checked })}
            />
            <span>Is Override Rule</span>
          </>
        )}
        {activeUser.isRecurrencyAdmin && template ? (
          <>
            <Checkbox
              checked={updates.internalOnly}
              onChange={(ev) => setUpdates({ ...updates, internalOnly: ev.target.checked })}
            />
            <span>Internal Only</span>
          </>
        ) : null}
      </FlexSpace>
    </Modal>
  );
}
